import { Check, Pencil, Trash2 } from "lucide-react";
import React, { memo, useCallback, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  PutChemicalTransportParams,
  useDeleteChemicalTransportData,
  usePutChemicalTransportData
} from "../../../../api/chemical";
import { Button } from "../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import {
  ChemicalTransportContextProps,
  ChemicalTransportRouteFormValues,
  Transportation,
  TransportType
} from "./chemicalTransport.types";
import { toRouteFormValues, validateChemicalRoute } from "./chemicalTransport.utils";

interface ChemicalTransportRouteItemProps extends ChemicalTransportContextProps {
  route: Transportation;
}

interface RouteViewProps {
  route: Transportation;
  TransportTypeList: TransportType[];
  lockStatus?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const RouteView: React.FC<RouteViewProps> = ({
  route,
  TransportTypeList,
  lockStatus,
  onEdit,
  onDelete
}) => {
  const typeValue = route.transportType ?? route.transportationType;
  const typeLabel = TransportTypeList.find(t => t.value === String(typeValue))?.text ?? "—";

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-py-1 tw-px-2 tw-rounded tw-bg-muted/30 tw-border tw-border-border/50">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-2 tw-text-[10px]">
        <span className="tw-font-medium tw-text-foreground">{typeLabel}</span>
        <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">{route.distance || 0} km</span>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-6 tw-w-6 tw-text-muted-foreground hover:tw-text-foreground"
              onClick={onEdit}
              disabled={lockStatus}
            >
              <Pencil className="tw-h-2.5 tw-w-2.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Edit</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button
        variant="ghost"
        size="icon"
        className="tw-h-6 tw-w-6 tw-text-destructive"
        onClick={onDelete}
        disabled={lockStatus}
      >
        <Trash2 className="tw-h-2.5 tw-w-2.5" />
      </Button>
    </div>
  );
};

interface RouteEditProps {
  form: UseFormReturn<ChemicalTransportRouteFormValues>;
  TransportTypeList: TransportType[];
  lockStatus?: boolean;
  onSave: () => void;
}

const RouteEdit: React.FC<RouteEditProps> = ({ form, TransportTypeList, lockStatus, onSave }) => (
  <div className="tw-border tw-border-primary/30 tw-rounded tw-p-2 tw-bg-background/50">
    <div className="tw-grid tw-grid-cols-[1fr_200px_36px] tw-gap-1.5 tw-items-center">
      <FormSelect
        control={form.control}
        name="transportationType"
        label=""
        placeholder="Type"
        options={TransportTypeList}
        disabled={lockStatus}
      />
      <FormInputNumber
        control={form.control}
        name="distance"
        label=""
        placeholder="0"
        rightContentEnable
        rightContent="km"
        disabled={lockStatus}
        removeDecimal={false}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="default"
              size="icon"
              className="tw-h-9 tw-w-9"
              onClick={onSave}
              disabled={lockStatus}
            >
              <Check className="tw-h-4 tw-w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Update changes</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  </div>
);

const ChemicalTransportRouteItem: React.FC<ChemicalTransportRouteItemProps> = ({
  route,
  lockStatus,
  TransportTypeList = [],
  chemicalId,
  type,
  supplierDataHandle,
  processId,
  onChemicalSave,
  refetchChemical,
  refetchTransportations
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<ChemicalTransportRouteFormValues>({
    defaultValues: toRouteFormValues(route)
  });

  const { mutate: updateTransport } = usePutChemicalTransportData();
  const { mutate: deleteTransport } = useDeleteChemicalTransportData();

  /**
   * Notifies parent to refetch transportation data from backend.
   * This prevents the chemical record from closing during transportation operations.
   */
  const notifyParent = useCallback(() => {
    refetchTransportations();

    if (onChemicalSave && processId) {
      onChemicalSave(processId);
    }
  }, [onChemicalSave, processId, refetchTransportations]);

  const handleStartEdit = useCallback(() => {
    form.reset(toRouteFormValues(route));
    setIsEditing(true);
  }, [form, route]);

  const handleUpdate = useCallback(() => {
    const values = form.getValues();
    if (!validateChemicalRoute(values, chemicalId)) return;
    if (!route._id) {
      errorView("Route ID is missing");
      return;
    }

    const payload: PutChemicalTransportParams = {
      supplierDataHandle: supplierDataHandle ?? false,
      type: type ?? "",
      chemicalTransportId: route._id,
      transportData: {
        transportType: values.transportationType,
        distance: parseFloat(values.distance)
      }
    };

    updateTransport(payload, {
      onSuccess: response => {
        successMessage("Transport route updated successfully");
        setIsEditing(false);

        // Trigger refetch of transportation data from backend
        notifyParent();
      },
      onError: (error: any) => {
        errorView(error?.msg || "Failed to update transport route");
      }
    });
  }, [form, chemicalId, route._id, supplierDataHandle, type, updateTransport, notifyParent]);

  const handleDelete = useCallback(() => {
    if (!route._id) return;
    deleteTransport(
      {
        supplierDataHandle: supplierDataHandle ?? false,
        type: type ?? "",
        chemicalTransportId: route._id,
        chemicalId: chemicalId ?? ""
      },
      {
        onSuccess: response => {
          successMessage("Transport route deleted successfully");

          // Trigger refetch of transportation data from backend
          notifyParent();
        },
        onError: (error: any) => {
          errorView(error?.msg || "Failed to delete transport route");
        }
      }
    );
  }, [deleteTransport, notifyParent, route._id, supplierDataHandle, type, chemicalId]);

  if (isEditing) {
    return (
      <RouteEdit
        form={form}
        TransportTypeList={TransportTypeList}
        lockStatus={lockStatus}
        onSave={handleUpdate}
      />
    );
  }

  return (
    <RouteView
      route={route}
      TransportTypeList={TransportTypeList}
      lockStatus={lockStatus}
      onEdit={handleStartEdit}
      onDelete={handleDelete}
    />
  );
};

export default memo(ChemicalTransportRouteItem);
